import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { AlertTriangle, Pause, Play } from "lucide-react";
import { fetchSessionRecording } from "../../redux/actionCreator";

/**
 * Minimal asciicast v2 player. Parses the cast file once, then schedules
 * `term.write(...)` calls relative to each event's timestamp. Supports
 * pause / resume / restart / speed.
 */
export default function CastPlayer({ session, onClose }) {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const timerRef = useRef(null);
  const eventsRef = useRef([]); // [t, kind, data]
  const idxRef = useRef(0);
  const startRef = useRef(0); // wallclock when playback was last started, ms
  const elapsedAtPauseRef = useRef(0); // accumulated cast-time when paused, seconds
  const speedRef = useRef(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const term = new XTerm({
      cursorBlink: false,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 13,
      theme: { background: "#0b1220" },
      scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    fit.fit();
    termRef.current = term;
    fitRef.current = fit;

    dispatch(fetchSessionRecording(session._id)).then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.message);
        setLoading(false);
        return;
      }
      const lines = res.body.split("\n").filter(Boolean);
      if (!lines.length) {
        setError("Empty recording");
        setLoading(false);
        return;
      }
      let header;
      try {
        header = JSON.parse(lines[0]);
      } catch {
        setError("Malformed recording");
        setLoading(false);
        return;
      }
      if (header.width && header.height) {
        try { term.resize(header.cols || header.width, header.rows || header.height); } catch {}
      }
      const events = [];
      for (let i = 1; i < lines.length; i++) {
        try {
          const ev = JSON.parse(lines[i]);
          if (Array.isArray(ev) && ev.length >= 3) events.push(ev);
        } catch { /* skip bad line */ }
      }
      eventsRef.current = events;
      idxRef.current = 0;
      elapsedAtPauseRef.current = 0;
      setDuration(events.length ? events[events.length - 1][0] : 0);
      setLoading(false);
      // Auto-play once loaded.
      play();
    });

    const onResize = () => fit.fit();
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      if (timerRef.current) clearTimeout(timerRef.current);
      term.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?._id]);

  const schedule = () => {
    const events = eventsRef.current;
    if (idxRef.current >= events.length) {
      setPlaying(false);
      return;
    }
    const ev = events[idxRef.current];
    const targetCast = ev[0];
    const playbackElapsed =
      elapsedAtPauseRef.current + (Date.now() - startRef.current) / 1000 * speedRef.current;
    const delayMs = Math.max(0, (targetCast - playbackElapsed) * 1000 / speedRef.current);
    timerRef.current = setTimeout(() => {
      const term = termRef.current;
      // Drain all events whose time has already passed (fast-forward).
      while (idxRef.current < events.length) {
        const cur = events[idxRef.current];
        const currentCastTime =
          elapsedAtPauseRef.current + (Date.now() - startRef.current) / 1000 * speedRef.current;
        if (cur[0] > currentCastTime) break;
        if (cur[1] === "o" && term) term.write(cur[2]);
        idxRef.current++;
      }
      setElapsed(
        elapsedAtPauseRef.current + (Date.now() - startRef.current) / 1000 * speedRef.current
      );
      schedule();
    }, delayMs);
  };

  const play = () => {
    if (!eventsRef.current.length) return;
    if (idxRef.current >= eventsRef.current.length) {
      // restart
      termRef.current?.clear();
      idxRef.current = 0;
      elapsedAtPauseRef.current = 0;
    }
    startRef.current = Date.now();
    setPlaying(true);
    schedule();
  };

  const pause = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    elapsedAtPauseRef.current += (Date.now() - startRef.current) / 1000 * speedRef.current;
    setElapsed(elapsedAtPauseRef.current);
    setPlaying(false);
  };

  const restart = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    termRef.current?.clear();
    idxRef.current = 0;
    elapsedAtPauseRef.current = 0;
    setElapsed(0);
    play();
  };

  const changeSpeed = (s) => {
    // Fold current progress into elapsedAtPauseRef so the new rate applies cleanly.
    if (playing) {
      elapsedAtPauseRef.current += (Date.now() - startRef.current) / 1000 * speedRef.current;
      startRef.current = Date.now();
    }
    speedRef.current = s;
    setSpeed(s);
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <Modal show={!!session} onHide={onClose} centered size="xl">
      <Modal.Header closeButton>
        <h4 className="m-0">
          Recording —{" "}
          <span className="text-muted">
            {session?.user?.name} · {session?.server_label} ({session?.server_host})
          </span>
        </h4>
      </Modal.Header>
      <Modal.Body style={{ background: "#0b1220" }}>
        {error ? (
          <div className="text-light text-center py-5">
            <div style={{ fontSize: 32, opacity: 0.6 }}><AlertTriangle size={32} /></div>
            <div className="mt-2">{error}</div>
          </div>
        ) : (
          <>
            {loading && (
              <div className="text-light text-center py-4">
                <Spinner animation="border" size="sm" /> Loading recording…
              </div>
            )}
            <div ref={containerRef} style={{ minHeight: 360 }} />
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <div className="d-flex gap-2 align-items-center">
          <Button size="sm" variant="primary" onClick={playing ? pause : play} disabled={loading || !!error}>
            {playing
              ? <span className="d-inline-flex align-items-center gap-1"><Pause size={14} /> Pause</span>
              : <span className="d-inline-flex align-items-center gap-1"><Play size={14} /> Play</span>}
          </Button>
          <Button size="sm" variant="light" onClick={restart} disabled={loading || !!error}>
            ↻ Restart
          </Button>
          <span className="text-muted small">
            {fmt(elapsed)} / {fmt(duration)}
          </span>
        </div>
        <div className="d-flex gap-1 align-items-center">
          <small className="text-muted me-2">Speed</small>
          {[0.5, 1, 2, 4].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={speed === s ? "primary" : "light"}
              onClick={() => changeSpeed(s)}
              disabled={loading || !!error}
            >
              {s}×
            </Button>
          ))}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
